import React, { FC, memo, useRef, useState } from 'react'

import { useFragment } from '@apollo/client'
import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  CommunityPost as BaseCommunityPost,
  CommunityPostProps,
} from '@factorialco/f0-react/dist/experimental'
import { Delete, PushPin, PushPinSolid } from '@factorialco/f0-react/icons/app'
import DOMPurify from 'dompurify'
import { emojiIndex } from 'emoji-mart'
import {
  CommunityPostDataFragment,
  CommunityPostDataFragmentDoc,
  CommunityPostListDocument,
  DeletePostDocument,
  GetPinnedPostsDocument,
  PostsGroupHeaderDocument,
  TogglePinPostDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import { useIntersectionObserver } from 'usehooks-ts'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useReactions } from 'modules/engagement/lib/reactions'

type Callbacks = {
  onGroupClick: (groupId: string) => void
  onPostClick: (postId: string) => void
  onCommentClick: (postId: string) => void
  onReactionInteraction?: (postId: string, reactionId: string) => void
}

type HookCallbacks = Callbacks & {
  onDeleteClick?: () => void
  actionButtonClickedRef?: React.MutableRefObject<boolean>
}

type Props = Callbacks & {
  id: number
  isPostPinningEnabled?: boolean
  onVisible?: () => void
}

const useNormalizedCommunityPostData = (
  data: CommunityPostDataFragment,
  callbacks?: HookCallbacks,
  isPostPinningEnabled?: boolean
): CommunityPostProps | undefined => {
  const { navigation } = useNavigation()

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const { onEmojiSelected } = useReactions({
    postId: data.id,
  })

  const author = data.author
  const authorEmployeeId = author?.employee?.id

  const preferredName = author?.employee?.preferredName

  const firstName = preferredName ? preferredName : author?.firstName
  const lastName = author?.lastName

  const [pinnedOverride, setPinnedOverride] = useState<boolean | undefined>(undefined)
  const isPinned = pinnedOverride !== undefined ? pinnedOverride : data.pinned

  const event =
    data.eventOn && data.startsAt && data.title && data.type === 'Posts::V2::Event'
      ? {
          date: stringToDate(data.startsAt, 'backend_datetime').toDate(),
          title: data.title,
          place: data.location ?? undefined,
          mediaUrl: data.coverImageUrl ?? undefined,
        }
      : undefined

  const getEmojiData = (code: string) => {
    let emojiData = emojiIndex.emojis?.[code]

    if (
      emojiData &&
      typeof emojiData === 'object' &&
      !('native' in emojiData) &&
      Object.keys(emojiData).every((k) => !isNaN(Number(k)))
    ) {
      const variants = Object.values(emojiData)
      emojiData = variants.find((e) => !e.skin) || variants[0]
    } else if (!emojiData) {
      const searchResults = emojiIndex.search(code)
      emojiData =
        searchResults?.find((e) => e.id === code || ('native' in e && e.native === code)) ||
        searchResults?.[0]
    }

    return emojiData
  }

  const [togglePin] = useMutation(TogglePinPostDocument, {
    extractorFn: (d) => d.postsMutations.togglePinPostsPost,
    refetchQueries: [CommunityPostListDocument, GetPinnedPostsDocument, PostsGroupHeaderDocument],
    awaitRefetchQueries: true,
  })

  const handleTogglePin = async () => {
    const result = await togglePin({ variables: { postId: data.id } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      const newPinned = handled?.data?.post?.pinned ?? !data.pinned
      setPinnedOverride(newPinned)

      const successMessage = newPinned
        ? i18n.t('posts.communities.post.pin_successfully')
        : i18n.t('posts.communities.post.unpin_successfully')

      app.setFlash({
        type: 'correct',
        message: successMessage,
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.post.pin_error'),
        time: 2000,
      })
    }
  }

  const dropdownItems = []

  if (data?._policies?.manage?.can) {
    if (isPostPinningEnabled) {
      const label = isPinned ? i18n.t('posts.communities.unpin') : i18n.t('posts.communities.pin')
      const icon = isPinned ? PushPinSolid : PushPin
      dropdownItems.push({
        label: label,
        icon: icon,
        onClick: () => {
          if (callbacks?.actionButtonClickedRef) {
            callbacks.actionButtonClickedRef.current = true
          }
          handleTogglePin()
        },
      })
    }

    dropdownItems.push({
      critical: true,
      icon: Delete,
      label: i18n.t('common.delete'),
      onClick: () => {
        if (callbacks?.actionButtonClickedRef) {
          callbacks.actionButtonClickedRef.current = true
        }
        callbacks?.onDeleteClick?.()
      },
    })
  }

  const actions =
    isPostPinningEnabled && data?._policies?.manage?.can
      ? [
          {
            label: isPinned ? i18n.t('posts.communities.unpin') : i18n.t('posts.communities.pin'),
            icon: isPinned ? PushPinSolid : PushPin,
            onClick: () => {
              if (callbacks?.actionButtonClickedRef) {
                callbacks.actionButtonClickedRef.current = true
              }
              handleTogglePin()
            },
          },
        ]
      : []

  return {
    id: `${data.id}`,
    dropdownItems: dropdownItems.length > 0 ? dropdownItems : undefined,
    actions,
    author:
      firstName && lastName && author
        ? {
            firstName,
            lastName,
            avatarUrl: buildUrl(author.avatar) ?? undefined,
            url: authorEmployeeId
              ? navigation.employees.id.fullPath({
                  id: authorEmployeeId,
                })
              : undefined,
          }
        : undefined,
    group: {
      title: data.group?.title ?? '',
      onClick: () => callbacks?.onGroupClick(`${data.group?.id}`),
    },
    createdAt: stringToDate(data.createdAt, 'backend_datetime').toDate(),
    title: data.title ?? '',
    description: DOMPurify.sanitize(data.description ?? '') ?? undefined,
    mediaUrl: data.coverImageUrl ?? undefined,
    event,
    counters: {
      views: `${data.visitsCount} ${i18n.tp('posts.visits.count', {
        count: data.visitsCount,
      })}`.toLowerCase(),
      comments: i18n.tp('posts.comments.count', {
        count: data.commentsConnection.totalCount,
      }),
    },
    noReactionsButton: !data.allowCommentsAndReactions,
    reactions: data.allowCommentsAndReactions
      ? {
          items: compact(
            data.newReactions.map((reaction) => {
              const emojiData = getEmojiData(reaction.code)

              let emoji = emojiData && 'native' in emojiData ? emojiData.native : undefined
              if (!emoji && /\p{Emoji}/u.test(reaction.code)) emoji = reaction.code
              if (!emoji) return

              const hasReacted = !!reaction.supporters.find(
                (supporter) => supporter.accessId === currentAccessId
              )

              return {
                emoji,
                initialCount: reaction.count,
                users: reaction.supporters.map(({ fullName }) => ({
                  name: fullName,
                })),
                onInteraction: onEmojiSelected,
                hasReacted,
              }
            })
          ),
          onInteraction: onEmojiSelected,
        }
      : undefined,
    inLabel: i18n.t('common.in'),
    comment: {
      label: i18n.t('posts.communities.write_comment'),
      onClick: () => callbacks?.onCommentClick(`${data.id}`),
    },
    onClick: () => {
      if (callbacks?.actionButtonClickedRef?.current) {
        callbacks.actionButtonClickedRef.current = false
        return
      }
      callbacks?.onPostClick(`${data.id}`)
    },
  }
}

const CommunityPost: FC<Props> = ({ id, isPostPinningEnabled, onVisible, ...callbacks }) => {
  const [showDeleteConfirm, toggleDeleteConfirm] = useToggle(false)
  const actionButtonClickedRef = useRef(false)

  const { data } = useFragment({
    fragment: CommunityPostDataFragmentDoc,
    fragmentName: 'CommunityPostData',
    from: {
      __typename: 'PostsPost',
      id,
      isPostPinningEnabled,
    },
  })

  const [deletePost] = useMutation(DeletePostDocument, {
    extractorFn: (d) => d.postsMutations.deletePostsPost,
    refetchQueries: [CommunityPostListDocument],
    awaitRefetchQueries: true,
  })

  const { ref } = useIntersectionObserver({
    threshold: 0.1,
    onChange(isIntersecting) {
      if (isIntersecting) {
        onVisible?.()
      }
    },
  })

  const normalizedData = useNormalizedCommunityPostData(
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    data as CommunityPostDataFragment,
    {
      ...callbacks,
      onDeleteClick: toggleDeleteConfirm(true),
      actionButtonClickedRef,
    },
    isPostPinningEnabled
  )

  const handleDeletePost = async () => {
    const result = await deletePost({ variables: { postId: id } })
    const handledResult = handleResult(result, () => {})

    toggleDeleteConfirm(false)()
    if (!handledResult.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.post.delete_error'),
      })
    }
  }

  if (!normalizedData) return null

  return (
    <Box ref={ref}>
      <BaseCommunityPost {...normalizedData} />
      {showDeleteConfirm && (
        <Modal
          illustration="alert"
          title={i18n.t('posts.communities.post.delete')}
          description={i18n.t('posts.communities.post.delete_description')}
          onClose={toggleDeleteConfirm(false)}
          disableFocusTrap
          footer={
            <>
              <F0Button
                variant="neutral"
                onClick={toggleDeleteConfirm(false)}
                label={i18n.t('common.cancel')}
              />
              <F0Button
                variant="default"
                onClick={handleDeletePost}
                label={i18n.t('posts.communities.post.delete_submit')}
              />
            </>
          }
        />
      )}
    </Box>
  )
}

export default memo(CommunityPost)
